import React, { useState, useEffect } from 'react';
import { Spin, notification, Table, Input } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { cloneDeep, sortBy } from 'lodash';

import Modal from '../../components/uiComponent/modal/index';
import InventoryWrapper from './style';
import { SubmitListingsFeed } from '../../slices/3-layer-products';

const BulkListingModal = ({ open, onHide, selectedItems }) => {
  const dispatch = useDispatch();
  const { submittingListingsFeed } = useSelector((state) => state.threeLayerProducts || {});

  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (open && selectedItems?.length) {
      const initialRows = cloneDeep(selectedItems).map((item) => ({
        id: item.id,
        sellerSku: item.sku,
        asin: item.vendorasin?.asin || '',
        title: item.item_name || '',
        price: item.price || '',
        quantity: item.item_quantity || item.total_quantity || 0,
        conditionType: item.item_condition || 'new_new'
      }));
      setRows(sortBy(initialRows, (r) => (r.price ? 1 : 0)));
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePriceChange = (id, value) => {
    setRows((prev) => prev.map((r) => r.id === id ? { ...r, price: value } : r));
  };

  const handleSubmit = async () => {
    const missingPrice = rows.some((r) => !r.price || Number(r.price) <= 0);
    if (missingPrice) {
      notification.error({
        top: 65,
        message: 'Missing Prices',
        description: 'All items must have a price greater than 0 before submitting.'
      });
      return;
    }

    const skuData = rows.map((r) => ({
      skuId: r.id,
      sellerSku: r.sellerSku,
      asin: r.asin,
      price: Number(r.price),
      quantity: r.quantity || 1,
      conditionType: r.conditionType || 'new_new'
    }));

    const result = await dispatch(SubmitListingsFeed({ skuData }));

    if (result?.payload?.success) {
      notification.success({
        top: 65,
        message: 'Listings Queued',
        description: 'Bulk listing job has been queued. Amazon will process it shortly.'
      });
      onHide();
    } else {
      notification.error({
        top: 65,
        message: 'Submission Failed',
        description: result?.payload?.message || 'Something went wrong. Please try again.'
      });
    }
  };

  const columns = [
    {
      title: 'SKU',
      dataIndex: 'sellerSku',
      width: 160,
      ellipsis: true
    },
    {
      title: 'ASIN',
      dataIndex: 'asin',
      width: 130,
      ellipsis: true
    },
    {
      title: 'Title',
      dataIndex: 'title',
      ellipsis: true,
      render: (val) => <span title={val}>{val || '—'}</span>
    },
    {
      title: 'Price (USD)',
      dataIndex: 'price',
      width: 130,
      render: (val, record) => (
        <Input
          value={val}
          type="number"
          min={0}
          step={0.01}
          onChange={(e) => handlePriceChange(record.id, e.target.value)}
          style={{ borderColor: (!val || Number(val) <= 0) ? '#F52C50' : undefined }}
          prefix="$"
        />
      )
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      width: 70,
      align: 'center'
    },
    {
      title: 'Condition',
      dataIndex: 'conditionType',
      width: 110,
      ellipsis: true
    }
  ];

  return (
    <Modal
      open={open}
      onHide={onHide}
      header={true}
      heading={`List on Amazon (${rows.length} SKU${rows.length !== 1 ? 's' : ''})`}
      closeButton={true}
      onClose={onHide}
      closeText="Cancel"
      saveText="Submit Listings"
      onSave={handleSubmit}
      buttonDisabled={submittingListingsFeed}
      size="lg"
    >
      <InventoryWrapper>
        <Spin spinning={submittingListingsFeed} tip="Submitting...">
          <p style={{ marginBottom: 12, color: '#4E5969', fontSize: 13 }}>
            Review SKU prices before submitting. Items without a price are highlighted — fill them in before submitting.
          </p>
          <Table
            dataSource={rows}
            columns={columns}
            rowKey="id"
            pagination={false}
            size="small"
            scroll={{ y: 400 }}
            rowClassName={(record) =>
              !record.price || Number(record.price) <= 0 ? 'ant-table-row-warning' : ''
            }
          />
        </Spin>
      </InventoryWrapper>
    </Modal>
  );
};

export default BulkListingModal;
